// mailchimpService.js
import apiClient from '../utils/apiClient';
import { handleApiError } from '../utils/apiService';
import { clearAdminCache } from './adminService';

// Define API endpoints using v2 routes
const ENDPOINTS = {
  SEND_INVITE: '/v2/mailchimp/send-user-invite',
  PASSWORD_RESET: '/v2/mailchimp/password-reset',
  TEST_EMAIL: '/v2/mailchimp/test-email',
  CONTACT_FORM: '/mailchimp/contact',  // Keep old route for contact form for now
  TRACK_LOGIN: '/v2/mailchimp/track-login',
  VERIFY_RESET_STATE: '/v2/auth/verify-reset-state',      
  COMPLETE_RESET: '/v2/auth/complete-password-reset',      
  BULK_EMAILS: '/v2/mailchimp/bulk-emails',
  TEMPLATES: '/v2/mailchimp/templates',
  ANNOUNCEMENT_AUDIENCE: '/v2/mailchimp/announcement-audience',
  SEND_ANNOUNCEMENT: '/v2/mailchimp/send-announcement',
  PASSWORD_CREATED: '/v2/auth/password-created',           
  GET_USER_BY_EMAIL: '/v2/auth/get-user-by-email',
  REFRESH_PASSWORD_TOKEN: '/v2/mailchimp/refresh-password-token',
  ACCOUNT_SETUP: '/v2/mailchimp/account-setup',
  SET_PASSWORD_DIRECT: '/v2/mailchimp/set-password-direct'
};

const mailchimpService = {
  // User invitations
  sendUserInvite: async (uid, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(`${ENDPOINTS.SEND_INVITE}/${uid}`, {}, {
        timeout: 30000
      });

      if (response.status === 429) {
        const error = new Error(response.message || 'Rate limit exceeded');
        error.status = 429;
        error.json = async () => response;
        throw error;
      }

      // Clear user cache since invite status will have changed
      clearAdminCache('users');

      return response;
    } catch (error) {
      console.error('Error sending Mailchimp invitation:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Password reset
  requestPasswordReset: async (email, enqueueSnackbar) => {
    try {
      // Call the API but don't store the response since we don't use it
      await apiClient.post(ENDPOINTS.PASSWORD_RESET, { email });
      
      // Always return success to avoid revealing which emails exist
      return { success: true };
    } catch (error) {
      console.error('Error requesting password reset:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      // Still return success to avoid revealing which emails exist
      return { success: true };
    }
  },

  // Test email
  sendTestEmail: async (enqueueSnackbar) => {
    try {
      const response = await apiClient.post(ENDPOINTS.TEST_EMAIL);
      return response;
    } catch (error) {
      console.error('Error sending test email:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Contact form submission
  submitContactForm: async (formData, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(ENDPOINTS.CONTACT_FORM, formData);
      return response;
    } catch (error) {
      console.error('Error submitting contact form:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Bulk email sending
  sendBulkEmails: async (userIds, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(ENDPOINTS.BULK_EMAILS, { userIds });
      return response;
    } catch (error) {
      console.error('Error sending bulk emails:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Announcement tool: list Mandrill templates available to send
  listAnnouncementTemplates: async (enqueueSnackbar) => {
    try {
      const response = await apiClient.get(ENDPOINTS.TEMPLATES);
      return response;
    } catch (error) {
      console.error('Error listing Mailchimp templates:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Announcement tool: preview recipient count/list for an audience
  getAnnouncementAudience: async (audience, enqueueSnackbar) => {
    try {
      const response = await apiClient.get(ENDPOINTS.ANNOUNCEMENT_AUDIENCE, {
        params: { audience }
      });
      return response;
    } catch (error) {
      console.error('Error fetching announcement audience:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Announcement tool: send a Mandrill template to a test address or the full audience
  sendAnnouncement: async ({ templateName, subject, audience, testEmail, retryEmails }, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(ENDPOINTS.SEND_ANNOUNCEMENT, {
        templateName,
        subject,
        audience,
        testEmail,
        retryEmails
      });
      return response;
    } catch (error) {
      console.error('Error sending announcement email:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Track the password creation process
  trackPasswordCreated: async (email, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(ENDPOINTS.PASSWORD_CREATED, { email });
      return response;
    } catch (error) {
      console.error('Error tracking password creation:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      // This is internal tracking, so we can ignore errors
      return { success: true };
    }
  },

  verifyResetState: async (state, email, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(ENDPOINTS.VERIFY_RESET_STATE, { 
        state, 
        email 
      });
      return response;
    } catch (error) {
      console.error('Error verifying reset state:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },
  
  completePasswordReset: async (state, uid, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(ENDPOINTS.COMPLETE_RESET, {
        state,
        uid
      });
      return response;
    } catch (error) {
      console.error('Error completing password reset:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },
  
  getUserByEmail: async (email, enqueueSnackbar) => {
    try {
      const response = await apiClient.post('/auth/get-user-by-email', { email });
      return response;
    } catch (error) {
      console.error('Error getting user by email:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  setPasswordDirectly: async (email, password, state) => {
    try {
      const response = await apiClient.post(ENDPOINTS.SET_PASSWORD_DIRECT, {
        password,
        state
      });
      
      // Clear user cache since password status will have changed
      clearAdminCache('users');
      
      return response;
    } catch (error) {
      console.error('Error setting password:', error);
      throw error;
    }
  },

  // Refresh password token
  refreshPasswordToken: async (state, email) => {
    try {
      const response = await apiClient.post(ENDPOINTS.REFRESH_PASSWORD_TOKEN, {
        state,
        email
      });
      return response;
    } catch (error) {
      console.error('Error refreshing password token:', error);
      throw error;
    }
  },

  // Account setup
  accountSetup: async (state, password, email) => {
    try {
      const response = await apiClient.post(ENDPOINTS.ACCOUNT_SETUP, {
        state,
        password,
        email
      });
      
      // Clear user cache since account setup status will have changed
      clearAdminCache('users');
      
      return response;
    } catch (error) {
      console.error('Error setting up account:', error);
      throw error;
    }
  },

  trackLogin: async (email, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(ENDPOINTS.TRACK_LOGIN, { email });
      
      // Clear user cache since login status will have changed
      clearAdminCache('users');
      
      return response;
    } catch (error) {
      console.error('❌ Error tracking login:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      // Don't throw - we don't want to disrupt login
      return { success: false, error: error.message };
    }
  },
};

export default mailchimpService;